import { memo, useCallback, useRef, useState } from "react";
import ReactPlayer from "react-player";
import Font from "components/Misc/Font";
import classNames from "classnames";
import { displayDuration } from "utils/time-util";
import { AudioPlayerContainer } from "./styled";
import { useMount } from "components/hooks";

function AudioPlayer({
  className,
  isPlaying,
  setIsPlaying,
  played,
  url,
  onEnd,
  onProgress,
  onReady,
  onError,
  skipOnProgressAtFirst,
  isError,
  ...props
}) {
  const playerRef = useRef();
  const progressRef = useRef();
  const skipOnProgressAtFirstRef = useRef(false);
  const playingBeforeTouchRef = useRef();
  const [duration, setDuration] = useState(0);
  progressRef.current = Math.min((played * 100) / duration, 100) || 0;

  useMount(() => {
    if (playerRef.current && played) {
      playerRef.current.seekTo(played);
    }
  });

  const onTouchStart = useCallback(() => {
    playingBeforeTouchRef.current = isPlaying;
    setIsPlaying(false);
  }, [isPlaying, setIsPlaying]);

  const onTouchEnd = useCallback(() => {
    if (playingBeforeTouchRef.current) {
      setIsPlaying(true);
    }
  }, [setIsPlaying]);

  const onSeek = useCallback(
    (evt) => {
      const seekTo = (evt.target.value / 100) * duration;
      if (playerRef.current) {
        playerRef.current.seekTo(seekTo);
      }
    },
    [duration]
  );

  const _onProgress = useCallback(
    (state) => {
      if (!skipOnProgressAtFirst || skipOnProgressAtFirstRef.current) {
        const isEnd = state.playedSeconds === duration;
        if (onProgress) {
          onProgress(state.playedSeconds, isEnd, state);
        }
      }

      if (!skipOnProgressAtFirstRef.current) {
        skipOnProgressAtFirstRef.current = true;
      }
    },
    [onProgress, skipOnProgressAtFirst, duration]
  );

  const _onEnd = useCallback(() => {
    setIsPlaying(false);
    if (onEnd) {
      setTimeout(onEnd, 50);
    }
  }, [onEnd, setIsPlaying]);

  return (
    <>
      <AudioPlayerContainer $ignoreProgress={!duration} $isError={isError}>
        <div className="button-action-item">
          <button
            type="button"
            className={classNames("action", { playing: isPlaying, pausing: !isPlaying })}
            onClick={() => setIsPlaying((p) => !p)}
          ></button>
        </div>
        <div className="seek-control">
          <input
            type="range"
            min={0}
            max={100}
            step="any"
            value={progressRef.current}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onChange={onSeek}
            style={{ "--percent": `${progressRef.current}%` }}
          />
        </div>
        <Font className="remain-duration" fontSize={14}>
          <small>
            {displayDuration(played)}/{displayDuration(duration)}
          </small>
        </Font>
      </AudioPlayerContainer>
      <ReactPlayer
        ref={playerRef}
        className="react-player"
        width="100%"
        height="100%"
        url={url}
        playing={isPlaying}
        progressInterval={100}
        onReady={onReady}
        onEnded={_onEnd}
        onError={(e) => onError(e)}
        onProgress={_onProgress}
        onDuration={setDuration}
        {...props}
      />
    </>
  );
}

export default memo(AudioPlayer);
